import get from 'lodash/get';
import { all, put, race, take, takeEvery } from 'redux-saga/effects';

import { APIConfiguration } from '@savgroup-front-common/configuration';
import { logCritical } from '@savgroup-front-common/configuration/appInsights/AppInsights';
import {
  ActionCreators as WorkflowActionCreators,
  ActionTypes as WorkflowActionTypes,
} from '@savgroup-front-common/core/domains/workflow';
import { executeSagaRequest } from '@savgroup-front-common/core/services';

import { getModelByIdRequest } from '../../api/Catalog/getModelByIdQuery';
import { getShippingLabelUrlRequest } from '../../api/Carrier/getShippingLabelUrlQuery';
import { getClaimContextSummaryRequest } from '../../api/Claim/getClaimContextSummaryQuery';
import { translateIrshRequest } from '../../api/Claim/translateIrshCommand';
import { getOpenedInvoiceByFileRequest } from '../../api/Payment/getOpenedInvoiceByFileQuery';
import { getQuoteInfoByFileIdRequest } from '../../api/Repairer/getQuoteInfoByFileIdQuery';
import { setQuoteClientAnswerRequest } from '../../api/Repairer/setQuoteClientAnswerCommand';
import { getFileActionsRequest } from '../../api/Workflow/getFileActionsQuery';
import { getCustomerFileFullSummaryRequest } from '../../api/Workflow/getCustomerFileFullSummaryQuery';
import { getFileStatesRequest } from '../../api/Workflow/getFileStatesQuery';
import { getOwnerFileRequest } from '../../api/Workflow/getOwnerFileQuery';
import { setFileActionRequest } from '../../api/Workflow/setFileActionCommand';

import * as ActionTypes from './actionTypes';

function* loadClaimRecievedScreenDataWorker({ payload: claimId }) {
  yield executeSagaRequest(
    ActionTypes.LOAD_CLAIM_CONFIRMATION_SCREEN_DATA,
    getClaimContextSummaryRequest({ claimId }),
  );

  yield put(ActionTypes.LOAD_CLAIM_CONFIRMATION_SCREEN_DATA.end());
}
export function* loadClaimRecievedScreenDataWacther() {
  yield takeEvery(
    ActionTypes.LOAD_CLAIM_CONFIRMATION_SCREEN_DATA.BASE,
    loadClaimRecievedScreenDataWorker,
  );
}

function* loadQuotesInfosWorker({ payload: fileId }) {
  yield executeSagaRequest(
    ActionTypes.LOAD_QUOTES_DATA,
    getQuoteInfoByFileIdRequest({ fileId }),
  );

  yield take(ActionTypes.LOAD_QUOTES_DATA.SUCCEEDED);

  yield put(ActionTypes.LOAD_QUOTES_DATA.end());
}
export function* loadQuotesInfosWacther() {
  yield takeEvery(ActionTypes.LOAD_QUOTES_DATA.BASE, loadQuotesInfosWorker);
}

function* loadInvoiceWorker({ payload }) {
  const fileId = get(payload, 'fileId');

  yield executeSagaRequest(
    ActionTypes.LOAD_INVOICE_DATA,
    getOpenedInvoiceByFileRequest({ fileId }),
    {
      fileId,
    },
  );

  yield put(ActionTypes.LOAD_INVOICE_DATA.end(fileId));
}
export function* loadInvoiceWatcher() {
  yield takeEvery(ActionTypes.LOAD_INVOICE_DATA.BASE, loadInvoiceWorker);
}

function* loadWorkflowActionWorker({ payload: fileId }) {
  if (!fileId) {
    yield put(ActionTypes.LOAD_WORKFLOW_ACTIONS.end());

    return;
  }

  yield executeSagaRequest(
    ActionTypes.LOAD_WORKFLOW_ACTIONS,
    getFileActionsRequest({ fileId }),
  );

  yield put(ActionTypes.LOAD_WORKFLOW_ACTIONS.end());
}
export function* loadWorkflowActionWatcher() {
  yield takeEvery(
    ActionTypes.LOAD_WORKFLOW_ACTIONS.BASE,
    loadWorkflowActionWorker,
  );
}

function* loadCustomerFileWorker({ payload }) {
  const { fileId, ownerId } = payload;

  if (!fileId || !ownerId) {
    yield put(ActionTypes.LOAD_CUSTOMER_FILE.end());

    return;
  }

  yield executeSagaRequest(
    ActionTypes.LOAD_CUSTOMER_FILE,
    getOwnerFileRequest({ ownerId, fileId }),
  );

  yield put(ActionTypes.LOAD_CUSTOMER_FILE.end());
}
export function* loadFileSummaryWatcher() {
  yield takeEvery(ActionTypes.LOAD_CUSTOMER_FILE.BASE, loadCustomerFileWorker);
}

function* loadCustomerFileSummaryWorker({ payload }) {
  const { fileId } = payload;

  if (!fileId) {
    yield put(ActionTypes.LOAD_CUSTOMER_FILE_SUMMARY.end());

    return;
  }

  yield executeSagaRequest(
    ActionTypes.LOAD_CUSTOMER_FILE_SUMMARY,
    getCustomerFileFullSummaryRequest({ fileId }),
  );
  yield put(ActionTypes.LOAD_CUSTOMER_FILE_SUMMARY.end());
}
export function* loadCustomerFileSummaryWatcher() {
  yield takeEvery(
    ActionTypes.LOAD_CUSTOMER_FILE_SUMMARY.BASE,
    loadCustomerFileSummaryWorker,
  );
}

function* loadModelInformationsWorker({ payload: modelId }) {
  if (!modelId) {
    yield put(ActionTypes.LOAD_MODEL_INFORMATIONS.end());

    return;
  }

  yield executeSagaRequest(
    ActionTypes.LOAD_MODEL_INFORMATIONS,
    getModelByIdRequest({ modelId }),
  );

  yield put(ActionTypes.LOAD_MODEL_INFORMATIONS.end());
}
export function* loadModelInformationsWatcher() {
  yield takeEvery(
    ActionTypes.LOAD_MODEL_INFORMATIONS.BASE,
    loadModelInformationsWorker,
  );
}

function* loadIRSHTranslationsWorker({
  payload: { issueId, reasonId, solutionId, warrantyTypeId },
}) {
  const meta = {
    issueId,
    reasonId,
    solutionId,
    warrantyTypeId,
  };

  yield executeSagaRequest(
    ActionTypes.LOAD_IRSH_TRANSLATIONS,
    translateIrshRequest(meta),
    meta,
  );

  yield put(ActionTypes.LOAD_IRSH_TRANSLATIONS.end());
}
export function* loadIRSHTranslationWatcher() {
  yield takeEvery(
    ActionTypes.LOAD_IRSH_TRANSLATIONS.BASE,
    loadIRSHTranslationsWorker,
  );
}

function* loadAvailableFileActionsWorker({ payload: fileId }) {
  if (!fileId) {
    yield put(ActionTypes.LOAD_FILE_STATE_HISTORY_LINES.end());

    return;
  }

  yield executeSagaRequest(
    ActionTypes.LOAD_FILE_STATE_HISTORY_LINES,
    getFileStatesRequest({ fileId }),
  );

  yield put(ActionTypes.LOAD_FILE_STATE_HISTORY_LINES.end());
}
export function* loadAvailableFileActionsWatcher() {
  yield takeEvery(
    ActionTypes.LOAD_FILE_STATE_HISTORY_LINES.BASE,
    loadAvailableFileActionsWorker,
  );
}

function* loadFileStatesWorker({ payload: fileId }) {
  if (!fileId) {
    yield put(ActionTypes.LOAD_FILE_STATES.end(fileId));

    return;
  }

  yield executeSagaRequest(
    ActionTypes.LOAD_FILE_STATES,
    getFileStatesRequest({ fileId }),
    {
      fileId,
    },
  );

  yield put(ActionTypes.LOAD_FILE_STATES.end(fileId));
}
export function* loadFileStatesWatcher() {
  yield takeEvery(ActionTypes.LOAD_FILE_STATES.BASE, loadFileStatesWorker);
}

function* submitWorkflowActionWorker({ payload = {} }) {
  const {
    module,
    fromState,
    toState,
    comment,
    expects,
    toCustomerComment,
    fileId,
  } = payload;

  const [, [saveExpectsSucceeded]] = yield all([
    put(WorkflowActionCreators.saveExpects({ fileId, expects })),
    race([
      take(WorkflowActionTypes.SAVE_EXPECTS.SUCCEEDED),
      take(WorkflowActionTypes.SAVE_EXPECTS.ERRORED),
    ]),
  ]);

  const meta = {
    indexer: fileId,
  };

  if (saveExpectsSucceeded) {
    yield all([
      executeSagaRequest(
        ActionTypes.SUBMIT_WORKFLOW_ACTION,
        setFileActionRequest({
          fileId,
          module,
          fromState,
          toState,
          comment,
          toCustomerComment,
        }),
      ),
      take(ActionTypes.SUBMIT_WORKFLOW_ACTION.SUCCEEDED),
    ]);
    yield put(ActionTypes.SUBMIT_WORKFLOW_ACTION.end(meta));
  }
}
export function* submitWorkflowActionWatcher() {
  yield takeEvery(
    ActionTypes.SUBMIT_WORKFLOW_ACTION.BASE,
    submitWorkflowActionWorker,
  );
}

function* getShippingLabelWorker({ payload }) {
  const { label, fileId, downloadWindow } = payload;
  const response = yield executeSagaRequest(
    ActionTypes.GET_SHIPPING_LABEL,
    getShippingLabelUrlRequest({
      fileId,
      moduleDefinitionId: label.moduleDefinitionId,
      moduleInstanceId: label.moduleInstanceId,
    }),
  );

  yield put(ActionTypes.GET_SHIPPING_LABEL.end());

  const shippingLabelUrl = get(response, 'payload.value');

  if (shippingLabelUrl) {
    const shippingLabel =
      APIConfiguration.carrier +
      shippingLabelUrl.replace('v1/', '').replace('{fileId}', fileId);

    downloadWindow.location.href = shippingLabel;
  } else {
    downloadWindow.close();
  }
}
export function* getShippingLabelUrlWatcher() {
  yield takeEvery(ActionTypes.GET_SHIPPING_LABEL.BASE, getShippingLabelWorker);
}

function* actOnQuoteWorker({ payload }) {
  const { quoteId, answer: clientAnswer } = payload;

  yield executeSagaRequest(
    ActionTypes.ACT_ON_QUOTE,
    setQuoteClientAnswerRequest({ clientAnswer, quoteId }),
  );
  yield put(ActionTypes.ACT_ON_QUOTE.end());
}
export function* actOnQuoteWacther() {
  yield takeEvery(ActionTypes.ACT_ON_QUOTE.BASE, actOnQuoteWorker);
}

export function* customerSaga() {
  try {
    yield all([
      loadFileSummaryWatcher(),
      loadCustomerFileSummaryWatcher(),
      loadModelInformationsWatcher(),
      loadIRSHTranslationWatcher(),
      loadAvailableFileActionsWatcher(),
      getShippingLabelUrlWatcher(),
      actOnQuoteWacther(),
      loadWorkflowActionWatcher(),
      submitWorkflowActionWatcher(),
      loadClaimRecievedScreenDataWacther(),
      loadQuotesInfosWacther(),
      loadInvoiceWatcher(),
      loadFileStatesWatcher(),
    ]);
  } catch (error) {
    logCritical(error);
  }
}

export default customerSaga;
